import * as React from 'react';
import * as styles from './pagination.module.scss';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons';
import { OutlinedInput } from 'kagr-ui';
import clsx from 'clsx';
import { IconButton } from '@material-ui/core';
import { calculatePagination, defaultPageSizeOptions } from './pagination-utils';
import { withStyles } from '@material-ui/styles';
import { blue } from '../../theme/colors';

interface Props {
  /** Total number of available pages */
  totalPages: number;
  /** Number of results per page */
  pageSize: number;
  /** Currently selected page */
  currentPage: number;
  /** Optionally disable the page selection buttons, i.e. while loading the next page */
  disabled?: boolean;
  /** Function to call on page change */
  handlePageChange: (page: number) => void;
  /** Function to call on page size change */
  handlePageSizeChange: (pageSize: number) => void;
}

const StyledIconButton = withStyles({
  root: {
    color: blue[600]
  }
})(IconButton);

/**
 * Pagination
 * Component to render paged navigation controls
 */
const Pagination = ({ totalPages, pageSize, currentPage, disabled, handlePageChange, handlePageSizeChange }: Props) => {
  const [inputPage, setInputPage] = React.useState<number | null>(null);
  const displayedPages = calculatePagination(totalPages, currentPage);

  // Clears out the number in input if the selectedPage is changed outside the input
  React.useEffect(() => {
    if (currentPage !== inputPage) {
      setInputPage(null);
    }
  }, [currentPage]);

  return (
    <div className={styles.root}>
      <div className={styles.pagesizeContainer}>
        <span>Show:</span>
        {defaultPageSizeOptions.map(option => (
          <button
            key={`pageOption-${option}`}
            onClick={() => handlePageSizeChange(option)}
            className={clsx(pageSize === option && styles.active)}
            aria-label={`Set page size to ${option}`}
          >
            {option}
          </button>
        ))}
      </div>
      <nav aria-label="pagination navigation" className={styles.pageNavigation}>
        <ul>
          <li>
            <StyledIconButton
              size="small"
              disabled={currentPage === 1 || disabled}
              onClick={() => handlePageChange(currentPage - 1)}
              aria-label="Go to previous page"
            >
              <KeyboardArrowLeft />
            </StyledIconButton>
          </li>
          {displayedPages.map(page => (
            <li key={page}>
              {typeof page === 'number' ? (
                <button
                  className={clsx(styles.pageNumber, page === currentPage && styles.active)}
                  onClick={() => handlePageChange(page)}
                  disabled={disabled}
                  aria-label={`Go to page ${page}`}
                >
                  {page}
                </button>
              ) : (
                '...'
              )}
            </li>
          ))}
          <li>
            <StyledIconButton
              color="inherit"
              size="small"
              disabled={currentPage === totalPages || disabled}
              onClick={() => handlePageChange(currentPage + 1)}
              aria-label="Go to next page"
            >
              <KeyboardArrowRight />
            </StyledIconButton>
          </li>
        </ul>
      </nav>
      <div className={styles.pageinputContainer}>
        <span>Go To Page:</span>
        <form
          onSubmit={event => {
            event.preventDefault();
            if (inputPage > totalPages) {
              setInputPage(totalPages);
              handlePageChange(totalPages);
            } else {
              handlePageChange(inputPage);
            }
          }}
        >
          <OutlinedInput
            id="outlined-page-input"
            margin="dense"
            variant="outlined"
            type="number"
            onChange={event => setInputPage(Number(event.target.value))}
            value={inputPage ? String(inputPage) : ''}
            tabIndex={1}
          />
        </form>
      </div>
    </div>
  );
};

export default Pagination;
